import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, NotebookPen, Quote } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { STEPS } from "../data/courseData";

export default function NotesPage() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/insight-notes")
      .then((r) => { if (!cancelled) setNotes(r.data || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Group by step → insight_index → list of {practice_index, practice_text, text, updated_at}
  const grouped = {};
  for (const n of notes) {
    if (!grouped[n.step]) grouped[n.step] = {};
    const key = n.insight_index;
    if (!grouped[n.step][key]) {
      grouped[n.step][key] = { insight_text: n.insight_text, items: [] };
    }
    grouped[n.step][key].items.push(n);
  }

  const totalNotes = notes.filter((n) => (n.text || "").trim().length > 0).length;
  const stepKeys = Object.keys(grouped).map(Number).sort((a, b) => a - b);

  return (
    <Layout>
      <button
        onClick={() => navigate("/settings")}
        className="text-sm text-[#9AAEC6] flex items-center gap-1 mb-4"
        data-testid="back-to-settings"
      >
        <ArrowLeft size={16} /> Settings
      </button>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="bps-eyebrow">Reflections</div>
        <h1 className="font-display text-3xl font-light tracking-tighter mt-1">My notes</h1>
        <p className="text-sm text-[#9AAEC6] mt-2 leading-relaxed">
          Everything you've written while exploring the insights — gathered in one quiet place.
        </p>

        <div className="mt-5 bps-card p-4 flex items-center gap-4" data-testid="notes-summary">
          <div className="w-12 h-12 rounded-full bg-[#22D3EE]/15 flex items-center justify-center shrink-0">
            <NotebookPen size={20} className="text-[#22D3EE]" strokeWidth={1.6} />
          </div>
          <div className="flex-1">
            <div className="font-display text-lg text-white">
              {totalNotes} {totalNotes === 1 ? "reflection" : "reflections"}
            </div>
            <div className="text-xs text-[#9AAEC6]">
              {totalNotes === 0
                ? "Open any insight and start writing — notes save automatically."
                : "Across the 4-week journey."}
            </div>
          </div>
        </div>

        {loading && (
          <div className="mt-6 bps-card p-6 text-center text-sm text-[#9AAEC6]" data-testid="notes-loading">
            Loading your reflections…
          </div>
        )}

        {!loading && totalNotes === 0 && (
          <div className="mt-6 bps-card p-6 text-center text-sm text-[#9AAEC6]" data-testid="notes-empty">
            No notes yet. Open <span className="text-[#22D3EE]">Practice 3 · Video Lesson</span> in any week,
            tap an insight, and write what comes up. It'll appear here automatically.
          </div>
        )}

        {!loading && totalNotes > 0 && (
          <div className="mt-6 space-y-6" data-testid="notes-list">
            {stepKeys.map((stepNum) => {
              const meta = STEPS.find((s) => s.step === stepNum);
              const insightKeys = Object.keys(grouped[stepNum]).map(Number).sort((a, b) => a - b);
              return (
                <section key={stepNum} data-testid={`notes-week-${stepNum}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <span
                      className="font-mono text-[10px] uppercase tracking-[0.22em] px-2 py-1 rounded-full border"
                      style={{ color: meta?.accent, borderColor: `${meta?.accent}55` }}
                    >
                      Week {stepNum}
                    </span>
                    <span className="text-sm text-white font-display tracking-tight">{meta?.title}</span>
                  </div>

                  <div className="space-y-4">
                    {insightKeys.map((ik) => {
                      const group = grouped[stepNum][ik];
                      const withText = group.items.filter((n) => (n.text || "").trim().length > 0);
                      if (withText.length === 0) return null;
                      return (
                        <div
                          key={ik}
                          className="bps-card p-5"
                          data-testid={`notes-insight-${stepNum}-${ik + 1}`}
                        >
                          <div className="flex items-start gap-2 mb-3">
                            <Quote size={14} className="text-[#22D3EE] mt-1 flex-shrink-0" />
                            <div className="font-display text-base text-white leading-snug">
                              {group.insight_text || `Insight ${ik + 1}`}
                            </div>
                          </div>
                          <ul className="space-y-3">
                            {withText
                              .sort((a, b) => a.practice_index - b.practice_index)
                              .map((n) => (
                                <li key={n.practice_index} className="text-sm">
                                  {n.practice_text && (
                                    <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#F59E0B] mb-1.5">
                                      Practice {n.practice_index + 1}
                                    </div>
                                  )}
                                  {n.practice_text && (
                                    <p className="text-xs text-[#9AAEC6] leading-relaxed mb-2 italic">
                                      {n.practice_text}
                                    </p>
                                  )}
                                  <div
                                    className="rounded-xl bg-[#03101F]/70 border border-white/10 px-3 py-2 text-white/90 leading-relaxed whitespace-pre-wrap"
                                    data-testid={`note-text-${stepNum}-${ik + 1}-${n.practice_index + 1}`}
                                  >
                                    {n.text}
                                  </div>
                                  {n.updated_at && (
                                    <div className="mt-1.5 text-[10px] font-mono uppercase tracking-[0.2em] text-[#7C8FA8]">
                                      {new Date(n.updated_at).toLocaleDateString(undefined, {
                                        month: "short",
                                        day: "numeric",
                                        year: "numeric",
                                      })}
                                    </div>
                                  )}
                                </li>
                              ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </motion.div>
    </Layout>
  );
}
